import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, FileText, Search, Banknote, Megaphone, Bell, Activity } from 'lucide-react';
import { KpiCard, QuickActionButton, SectionHeader, StatBadge, AiActivityFeedItem, EmptyState } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { KpiData, DashboardPeriod, PortfolioHealth, QuickActionDef, AiActivityEvent } from '@/types/dashboard';

// ---- Helpers ----

function getJwt(): string {
  return localStorage.getItem('crm_dev_token') ?? '';
}

// ---- Mock data generators ----

function generateMockAiEvents(): AiActivityEvent[] {
  const now = Date.now();
  return [
    { id: 'evt-01', type: 'review_queue', description: 'Loan underwriting summary reviewed', memberName: 'Sarah Mitchell', timestamp: new Date(now - 5 * 60000).toISOString(), opsScanStatus: 'passed', staffName: 'J. Adams' },
    { id: 'evt-02', type: 'chat_session', description: 'Member inquiry: auto loan refinance options', memberName: 'Marcus Rivera', timestamp: new Date(now - 12 * 60000).toISOString(), opsScanStatus: 'passed', staffName: 'K. Chen' },
    { id: 'evt-03', type: 'transcript_analysis', description: 'Call transcript analyzed — 3 action items extracted', memberName: 'Priya Nair', timestamp: new Date(now - 25 * 60000).toISOString(), opsScanStatus: 'passed', staffName: null },
    { id: 'evt-04', type: 'review_queue', description: 'Profile field suggestion: lifecycle stage update', memberName: 'Derek Fontaine', timestamp: new Date(now - 45 * 60000).toISOString(), opsScanStatus: 'passed', staffName: 'L. Park' },
    { id: 'evt-05', type: 'chat_session', description: 'Member inquiry: CD maturity options', memberName: 'Linda Okafor', timestamp: new Date(now - 60 * 60000).toISOString(), opsScanStatus: 'passed', staffName: 'J. Adams' },
    { id: 'evt-06', type: 'transcript_analysis', description: 'Live listener session finalized — summary generated', memberName: 'James Tran', timestamp: new Date(now - 90 * 60000).toISOString(), opsScanStatus: 'passed', staffName: null },
    { id: 'evt-07', type: 'review_queue', description: 'Action item: follow up on HELOC application', memberName: 'James Tran', timestamp: new Date(now - 120 * 60000).toISOString(), opsScanStatus: 'passed', staffName: 'K. Chen' },
    { id: 'evt-08', type: 'chat_session', description: 'Member risk assessment discussion', memberName: 'Sarah Mitchell', timestamp: new Date(now - 180 * 60000).toISOString(), opsScanStatus: 'pending', staffName: 'L. Park' },
  ];
}

function generateMockKpis(period: DashboardPeriod): KpiData[] {
  // Slight variation per period to give visual feedback when switching
  const periodMultiplier: Record<DashboardPeriod, number> = {
    mtd: 0.8,
    qtd: 0.9,
    ytd: 1.0,
    last12m: 1.1,
  };
  const m = periodMultiplier[period];

  return [
    {
      id: 'roa',
      label: 'ROA',
      value: `${(1.12 * m).toFixed(2)}%`,
      delta: 0.08,
      deltaDirection: 'up',
      sparkData: [
        { label: 'Mon1', value: 1.02 },
        { label: 'Mon2', value: 1.05 },
        { label: 'Mon3', value: 1.08 },
        { label: 'Mon4', value: 1.06 },
        { label: 'Mon5', value: 1.10 },
        { label: 'Mon6', value: 1.12 },
      ],
      format: 'percent',
    },
    {
      id: 'loan_growth',
      label: 'Loan Growth',
      value: `$${(4.2 * m).toFixed(1)}M`,
      delta: 3.2,
      deltaDirection: 'up',
      sparkData: [
        { label: 'Mon1', value: 3200000 },
        { label: 'Mon2', value: 3500000 },
        { label: 'Mon3', value: 3800000 },
        { label: 'Mon4', value: 3600000 },
        { label: 'Mon5', value: 3900000 },
        { label: 'Mon6', value: 4200000 },
      ],
      format: 'currency',
    },
    {
      id: 'member_growth',
      label: 'Member Growth',
      value: `+${Math.round(142 * m)}`,
      delta: 1.8,
      deltaDirection: 'up',
      sparkData: [
        { label: 'Mon1', value: 95 },
        { label: 'Mon2', value: 108 },
        { label: 'Mon3', value: 120 },
        { label: 'Mon4', value: 115 },
        { label: 'Mon5', value: 132 },
        { label: 'Mon6', value: 142 },
      ],
      format: 'count',
    },
    {
      id: 'delinquency',
      label: 'Delinquency Rate',
      value: `${(0.34 / m).toFixed(2)}%`,
      delta: -0.05,
      deltaDirection: 'down',
      sparkData: [
        { label: 'Mon1', value: 0.42 },
        { label: 'Mon2', value: 0.40 },
        { label: 'Mon3', value: 0.38 },
        { label: 'Mon4', value: 0.37 },
        { label: 'Mon5', value: 0.35 },
        { label: 'Mon6', value: 0.34 },
      ],
      format: 'percent',
    },
    {
      id: 'efficiency',
      label: 'Efficiency Ratio',
      value: `${(68.4 / m).toFixed(1)}%`,
      delta: -1.2,
      deltaDirection: 'down',
      sparkData: [
        { label: 'Mon1', value: 72.0 },
        { label: 'Mon2', value: 71.2 },
        { label: 'Mon3', value: 70.5 },
        { label: 'Mon4', value: 70.1 },
        { label: 'Mon5', value: 69.0 },
        { label: 'Mon6', value: 68.4 },
      ],
      format: 'percent',
    },
    {
      id: 'ai_queue',
      label: 'AI Queue',
      value: `${Math.round(23 * m)}`,
      delta: 12,
      deltaDirection: 'up',
      sparkData: [
        { label: 'Mon1', value: 8 },
        { label: 'Mon2', value: 11 },
        { label: 'Mon3', value: 15 },
        { label: 'Mon4', value: 18 },
        { label: 'Mon5', value: 21 },
        { label: 'Mon6', value: 23 },
      ],
      format: 'count',
    },
  ];
}

const MOCK_PORTFOLIO_HEALTH: PortfolioHealth = {
  loanPipeline: { applied: 12, underwriting: 8, approved: 15, funded: 42, denied: 3 },
  campaigns: { active: 3, paused: 1, completed: 7 },
  alerts: { total: 18, critical: 2 },
};

// ---- Quick actions ----

const QUICK_ACTIONS: QuickActionDef[] = [
  { id: 'review', label: 'Review Queue', icon: ClipboardCheck, path: '/members', color: 'violet' },
  { id: 'transcript', label: 'New Transcript', icon: FileText, path: '/members', color: 'teal' },
  { id: 'search', label: 'Member Search', icon: Search, path: '/members', color: 'blue' },
];

// ---- Period selector options ----

const PERIOD_OPTIONS: { value: DashboardPeriod; label: string }[] = [
  { value: 'mtd', label: 'MTD' },
  { value: 'qtd', label: 'QTD' },
  { value: 'ytd', label: 'YTD' },
  { value: 'last12m', label: 'Last 12 months' },
];

// ---- DashboardPage ----

export function DashboardPage(): React.ReactElement {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<DashboardPeriod>('ytd');
  const [kpis, setKpis] = useState<KpiData[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioHealth | null>(null);
  const [aiEvents, setAiEvents] = useState<AiActivityEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchDashboardData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${getJwt()}` };
      const [membersRes, reviewQueueRes, loansRes, campaignsRes] = await Promise.all([
        fetch('/api/members?page=1&pageSize=1', { headers }),
        fetch('/api/review-queue', { headers }),
        fetch('/loans', { headers }),
        fetch('/campaigns', { headers }),
      ]);

      // Extract counts from API responses for KPI data
      const membersData = membersRes.ok ? (await membersRes.json() as { total?: number }) : null;
      const reviewQueueData = reviewQueueRes.ok ? (await reviewQueueRes.json() as unknown[]) : null;
      const loansData = loansRes.ok ? (await loansRes.json() as unknown[]) : null;
      const campaignsData = campaignsRes.ok ? (await campaignsRes.json() as unknown[]) : null;

      // Map review queue items to AiActivityEvent format
      if (reviewQueueData !== null && Array.isArray(reviewQueueData)) {
        const mappedEvents: AiActivityEvent[] = (reviewQueueData as Array<Record<string, unknown>>).slice(0, 20).map((item, i) => ({
          id: `rq-${i}`,
          type: 'review_queue' as const,
          description: String(item['text'] ?? 'Review queue item'),
          memberName: item['memberName'] ? String(item['memberName']) : null,
          timestamp: String(item['createdAt'] ?? new Date().toISOString()),
          opsScanStatus: 'passed' as const,
          staffName: item['staffName'] ? String(item['staffName']) : null,
        }));
        setAiEvents(mappedEvents.length > 0 ? mappedEvents : generateMockAiEvents());
      } else {
        setAiEvents(generateMockAiEvents());
      }

      // Build KPIs from live data where available, fallback to mock for missing metrics
      const liveMockKpis = generateMockKpis(period);

      if (reviewQueueData !== null && Array.isArray(reviewQueueData)) {
        const queueItem = liveMockKpis.find((k) => k.id === 'ai_queue');
        if (queueItem !== undefined) {
          queueItem.value = String(reviewQueueData.length);
        }
      }

      if (membersData?.total !== undefined) {
        // member_growth reflects recent additions; keep mock delta, update display value
        const mgItem = liveMockKpis.find((k) => k.id === 'member_growth');
        if (mgItem !== undefined) {
          mgItem.value = `+${membersData.total}`;
        }
      }

      setKpis(liveMockKpis);

      // Build portfolio health from live loan/campaign data
      if (loansData !== null && campaignsData !== null) {
        const loanArr = loansData as Array<{ stage?: string }>;
        const campaignArr = campaignsData as Array<{ status?: string }>;

        const livePortfolio: PortfolioHealth = {
          loanPipeline: {
            applied: loanArr.filter((l) => l.stage === 'applied').length,
            underwriting: loanArr.filter((l) => l.stage === 'underwriting').length,
            approved: loanArr.filter((l) => l.stage === 'approved').length,
            funded: loanArr.filter((l) => l.stage === 'funded').length,
            denied: loanArr.filter((l) => l.stage === 'denied').length,
          },
          campaigns: {
            active: campaignArr.filter((c) => c.status === 'active').length,
            paused: campaignArr.filter((c) => c.status === 'paused').length,
            completed: campaignArr.filter((c) => c.status === 'completed').length,
          },
          alerts: MOCK_PORTFOLIO_HEALTH.alerts,
        };
        setPortfolio(livePortfolio);
      } else {
        setPortfolio(MOCK_PORTFOLIO_HEALTH);
      }
    } catch {
      // API not running in dev — fall back to mock data
      setKpis(generateMockKpis(period));
      setPortfolio(MOCK_PORTFOLIO_HEALTH);
      setAiEvents(generateMockAiEvents());
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    void fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="flex flex-col gap-6">
      {/* Header row: title + period selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink font-heading">Executive Dashboard</h1>
          <p className="text-sm text-ink-muted mt-0.5">Credit union performance at a glance</p>
        </div>
        <div className="flex items-center gap-1 bg-white rounded-lg border border-line p-1 shadow-sm">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPeriod(opt.value)}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                period === opt.value
                  ? 'bg-brand text-on-brand'
                  : 'text-ink-soft hover:bg-sunken',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Grid — 6 cards in 3x2 on lg, 2-col on md, 1-col on sm */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.id} data={kpi} />
        ))}
        {loading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-line shadow-sm p-4 animate-pulse"
            >
              <div className="h-3 bg-line rounded w-20 mb-3" />
              <div className="h-7 bg-line rounded w-24 mb-3" />
              <div className="h-10 bg-sunken rounded w-full" />
            </div>
          ))}
      </div>

      {/* Quick Actions row */}
      <div>
        <SectionHeader title="Quick Actions" />
        <div className="flex flex-wrap gap-3 mt-3">
          {QUICK_ACTIONS.map((action) => (
            <QuickActionButton
              key={action.id}
              icon={action.icon}
              label={action.label}
              color={action.color}
              onClick={() => navigate(action.path)}
            />
          ))}
        </div>
      </div>

      {/* AI Activity Feed */}
      <div>
        <SectionHeader title="AI Activity Feed" linkText="View all" linkTo="/members" />
        <div className="bg-white rounded-xl border border-line shadow-sm mt-3 divide-y divide-sunken">
          {loading && aiEvents.length === 0 && (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-line" />
                <div className="flex-1">
                  <div className="h-3.5 bg-line rounded w-48 mb-1.5" />
                  <div className="h-3 bg-sunken rounded w-32" />
                </div>
                <div className="h-4 bg-sunken rounded w-16" />
              </div>
            ))
          )}
          {aiEvents.length === 0 && !loading ? (
            <div className="p-8">
              <EmptyState
                icon={Activity}
                headline="No AI activity yet"
                body="AI-generated insights, review queue events, and chat sessions will appear here as staff interact with the system."
              />
            </div>
          ) : (
            aiEvents.map(event => (
              <AiActivityFeedItem key={event.id} event={event} className="px-4" />
            ))
          )}
        </div>
      </div>

      {/* Portfolio Health Panel */}
      <div>
        <SectionHeader title="Portfolio Health" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">

          {/* Loan Pipeline card */}
          <div
            className="bg-white rounded-xl border border-line shadow-sm p-4 cursor-pointer hover:border-brand transition-colors"
            onClick={() => navigate('/loans')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/loans'); }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Banknote className="h-4 w-4 text-pass" />
              <h3 className="text-sm font-semibold text-ink">Loan Pipeline</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {portfolio !== null && (
                <>
                  <StatBadge count={portfolio.loanPipeline.applied} label="Applied" color="blue" />
                  <StatBadge count={portfolio.loanPipeline.underwriting} label="UW" color="violet" />
                  <StatBadge count={portfolio.loanPipeline.approved} label="Approved" color="teal" />
                  <StatBadge count={portfolio.loanPipeline.funded} label="Funded" color="gray" />
                  <StatBadge count={portfolio.loanPipeline.denied} label="Denied" color="red" />
                </>
              )}
            </div>
          </div>

          {/* Campaigns card */}
          <div
            className="bg-white rounded-xl border border-line shadow-sm p-4 cursor-pointer hover:border-brand transition-colors"
            onClick={() => navigate('/campaigns')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/campaigns'); }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="h-4 w-4 text-running" />
              <h3 className="text-sm font-semibold text-ink">Campaigns</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {portfolio !== null && (
                <>
                  <StatBadge count={portfolio.campaigns.active} label="Active" color="teal" />
                  <StatBadge count={portfolio.campaigns.paused} label="Paused" color="amber" />
                  <StatBadge count={portfolio.campaigns.completed} label="Done" color="gray" />
                </>
              )}
            </div>
          </div>

          {/* Alerts card */}
          <div
            className="bg-white rounded-xl border border-line shadow-sm p-4 cursor-pointer hover:border-brand transition-colors"
            onClick={() => navigate('/alerts')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/alerts'); }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Bell className="h-4 w-4 text-warn" />
              <h3 className="text-sm font-semibold text-ink">Alerts</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {portfolio !== null && (
                <>
                  <StatBadge count={portfolio.alerts.total} label="Total" color="amber" />
                  <StatBadge count={portfolio.alerts.critical} label="Critical" color="red" />
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
